import React, { useEffect, useRef, useState } from 'react';
import { IoChevronDown, IoContract, IoExpand, IoDesktopOutline, IoVolumeHigh, IoVolumeMute } from 'react-icons/io5';
import { useLanguage } from '../i18n/LanguageContext';
import { VideoTile } from './VideoGridOverlay';
import { useOverlayFullscreen } from '../hooks/useOverlayFullscreen';
import { useLayoutMode } from '../hooks/useLayoutMode';
import { useAutoHideChrome } from '../hooks/useAutoHideChrome';

// Own preference, separate from VideoWatchOverlay's videoWatchVolume - a
// screen share's audio (system/app sound) and a watch-together video's
// audio are different enough contexts that sharing one slider position
// between them would be surprising.
const VOLUME_STORAGE_KEY = 'screenShareVolume';

function loadStoredVolume() {
  const stored = parseFloat(localStorage.getItem(VOLUME_STORAGE_KEY));
  return Number.isFinite(stored) ? Math.min(1, Math.max(0, stored)) : 1;
}

// Spotlight for a live screen-share track - same overlay footprint/CSS
// classes as VideoWatchOverlay (.VideoOverlay/.VideoWatchSurfaceWrap/
// .VideoWatchCameraStrip), but driven by a real LiveKit Track via
// attach()/detach() (see VideoTile) instead of an <iframe>/<video src>, and
// with no play/pause/seek - it's a live feed, not a seekable clip.
export default function ScreenShareOverlay({
  roomLabel, screenSharer, screenTrack, screenAudioTrack, isLocal,
  participants, videoTracks, occupants, onStop, onClose
}) {
  const { t } = useLanguage();
  const overlayRef = useRef(null);
  const videoRef = useRef(null);
  const { isFullscreen, pseudoFullscreen, toggleFullscreen } = useOverlayFullscreen(overlayRef);
  // Same immersive phone fullscreen as VideoWatchOverlay - a live share is
  // always "playing", so the controls always auto-hide there.
  const { isMobile } = useLayoutMode();
  const immersive = isMobile && isFullscreen;
  const { chromeHidden, poke, toggle: toggleChrome } = useAutoHideChrome(immersive, true);
  const [volume, setVolume] = useState(loadStoredVolume);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !screenTrack) return undefined;
    screenTrack.attach(el);
    return () => { screenTrack.detach(el); };
  }, [screenTrack]);

  // The sharer's own audio already plays acoustically off their own screen -
  // attaching it back to their own <video> would double it locally (and
  // isn't needed for LiveKit's publish path either way, unlike a remote
  // viewer who has no other way to hear it). Same attach()-accepts-audio-
  // tracks-too pattern as the video track above, just conditional on isLocal.
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !screenAudioTrack || isLocal) return undefined;
    screenAudioTrack.attach(el);
    return () => { screenAudioTrack.detach(el); };
  }, [screenAudioTrack, isLocal]);

  // Local-only, never sent anywhere - each viewer controls their own
  // playback volume, same "everyone hears their own mix" reasoning as
  // VideoWatchOverlay's volume slider. Meaningless for the local sharer
  // (their own <video> is always muted, see the audio-attach effect above),
  // so this only ever runs/renders for remote viewers.
  //
  // Goes through the LiveKit track's own setVolume(), NOT
  // videoRef.current.volume directly - useVoiceConnection's Room is created
  // with webAudioMix: true, and RemoteAudioTrack.attach() under that mode
  // forces the attached element's own .volume to 0/muted and instead routes
  // real playback through an internal Web Audio GainNode that only
  // track.setVolume() controls. Setting the element's .volume directly (as
  // this used to) silently did nothing - the slider looked like it worked
  // but audio kept playing at the gain node's untouched default (full
  // volume), even dragged all the way to 0.
  useEffect(() => {
    if (isLocal || !screenAudioTrack) return;
    screenAudioTrack.setVolume(volume);
  }, [volume, isLocal, screenAudioTrack]);

  const handleVolumeChange = (e) => {
    const vol = parseFloat(e.target.value);
    setVolume(vol);
    localStorage.setItem(VOLUME_STORAGE_KEY, String(vol));
  };

  const cameraTiles = (participants || []).filter((p) => !p.identity.startsWith('bot:') && p.identity !== screenSharer.identity);
  const anyCamera = cameraTiles.some((p) => p.cameraEnabled);

  return (
    <div className={`VideoOverlay ScreenShareOverlay glass${pseudoFullscreen ? ' VideoOverlayPseudoFullscreen' : ''}${immersive ? ' VideoOverlayImmersive' : ''}${chromeHidden ? ' VideoOverlayChromeHidden' : ''}`} ref={overlayRef}>
      <div className="VideoOverlayHeader" onPointerDownCapture={immersive ? poke : undefined}>
        <span>{t('voice.screenSharingTitle', { name: screenSharer.name || screenSharer.identity })}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {isLocal && (
            <button onClick={onStop} className="ScreenShareStopBtn cancelColor" title={t('voice.stopScreenShare')}>
              <IoDesktopOutline />
            </button>
          )}
          <button onClick={onClose} className="VideoOverlayCloseBtn" title={t('voice.hideVideo')}><IoChevronDown /></button>
        </div>
      </div>
      <div className="VideoWatchSurfaceWrap">
        {immersive && <div className="VideoImmersiveTapLayer" onClick={toggleChrome} />}
        {/* muted for the local sharer only - see the audio-attach effect
            above for why a remote viewer must NOT be muted here. */}
        <video ref={videoRef} className="VideoWatchSurface VideoWatchSurfaceNative" autoPlay playsInline muted={isLocal} />
      </div>
      {anyCamera && (
        <div className="VideoWatchCameraStrip">
          {cameraTiles.map((p) => (
            <VideoTile
              key={p.identity}
              participant={p}
              track={videoTracks[p.identity]}
              occupant={occupants.find((o) => o.userId === p.identity)}
            />
          ))}
        </div>
      )}
      <div className="VideoWatchControls" onPointerDownCapture={immersive ? poke : undefined}>
        {!isLocal && (
          <div className="VideoWatchVolume">
            {volume === 0 ? <IoVolumeMute /> : <IoVolumeHigh />}
            <input type="range" min="0" max="1" step="0.01" value={volume} onChange={handleVolumeChange} />
          </div>
        )}
        <button onClick={toggleFullscreen} className="secondaryColor" title={isFullscreen ? t('music.exitFullscreen') : t('music.fullscreen')}>
          {isFullscreen ? <IoContract /> : <IoExpand />}
        </button>
      </div>
    </div>
  );
}
